/**
 * Full activity timeline page — browsable, filterable, paginated.
 * Merges: comments, acceptances, downloads into one chronological feed.
 */

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@refinedev/core";
import { usePortalContact } from "@/hooks/usePortalContact";
import { useDownloadTracker } from "@/hooks/useDownloadTracker";
import { renderMentionsVault as renderMentions } from "@/utils/mentions";
import { timeAgo, formatDate } from "@/utils/timeAgo";

type EventType = "comment" | "accept" | "read" | "download" | "all";

type TimelineEntry = {
  id: string;
  type: "comment" | "accept" | "read" | "download";
  actor: string;
  body: string;
  target?: { name: string; path: string };
  time: string;
  dateKey: string; // YYYY-MM-DD for grouping
};

const PAGE_SIZE = 20;

const dotClass: Record<string, string> = {
  comment: "vault-timeline-dot-comment",
  accept: "vault-timeline-dot-accept",
  read: "vault-timeline-dot-read",
  download: "vault-timeline-dot-download",
};
const dotIcon: Record<string, string> = { comment: "✉", accept: "✓", read: "●", download: "↓" };
const actionLabel: Record<string, string> = { comment: "ha commentato su", accept: "ha accettato", read: "ha letto", download: "ha scaricato" };

const FILTER_OPTIONS: { key: EventType; label: string }[] = [
  { key: "all", label: "Tutti" },
  { key: "comment", label: "Commenti" },
  { key: "accept", label: "Accettazioni" },
  { key: "read", label: "Letture" },
  { key: "download", label: "Download" },
];

export const TimelinePage: React.FC = () => {
  const navigate = useNavigate();
  const { contactName } = usePortalContact();
  const { downloads } = useDownloadTracker();
  const [typeFilter, setTypeFilter] = useState<EventType>("all");
  const [page, setPage] = useState(0);

  const portalName = contactName || "Tu";

  // Data sources
  const { data: commentsData } = useList({
    resource: "comments",
    pagination: { pageSize: 100 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });

  // coreBOS portal session handles data scoping automatically
  const { data: acceptanceData } = useList({
    resource: "acceptance",
    pagination: { pageSize: 200 },
  });

  const { data: policiesData } = useList({
    resource: "policies",
    pagination: { pageSize: 50 },
  });

  const { data: contentData } = useList({
    resource: "content",
    pagination: { pageSize: 50 },
  });

  const policies = (policiesData?.data || []) as any[];
  const contents = (contentData?.data || []) as any[];
  const comments = (commentsData?.data || []) as any[];
  const acceptances = (acceptanceData?.data || []) as any[];

  const policyById = new Map(policies.map((p: any) => [p.id, p.title || "Policy"]));
  const contentById = new Map(contents.map((c: any) => [c.id, c.title || "Contenuto"]));

  const resolveTarget = (id?: string): { name: string; path: string } | null => {
    if (!id) return null;
    if (id.startsWith("73x") && policyById.has(id)) return { name: policyById.get(id)!, path: `/digibox/policies/${id}` };
    if (id.startsWith("85x") && contentById.has(id)) return { name: contentById.get(id)!, path: `/digibox/content` };
    return null;
  };

  // Build all entries
  const allEntries = useMemo(() => {
    const entries: TimelineEntry[] = [];

    for (const c of comments) {
      const target = resolveTarget(c.related_to);
      const time = c.createdtime || "";
      entries.push({
        id: `c-${c.id}`,
        type: "comment",
        actor: portalName,
        body: c.commentcontent || "",
        target: target || undefined,
        time,
        dateKey: time.slice(0, 10),
      });
    }

    for (const a of acceptances) {
      const status = a.acceptance_status;
      if (!status || status === "Pending") continue;
      const target = resolveTarget(a.acctopolicy) || resolveTarget(a.acceptancetocontent);
      const time = a.acclastdate || a.modifiedtime || "";
      entries.push({
        id: `a-${a.id}`,
        type: status === "Read" ? "read" : "accept",
        actor: a.acctitle || portalName,
        body: "",
        target: target || undefined,
        time,
        dateKey: time.slice(0, 10),
      });
    }

    for (const [docId, dl] of Object.entries(downloads)) {
      const time = dl.downloadedAt || "";
      entries.push({
        id: `dl-${docId}`,
        type: "download",
        actor: portalName,
        body: "",
        target: { name: dl.filename || docId, path: "/digibox/archive" },
        time,
        dateKey: time.slice(0, 10),
      });
    }

    entries.sort((a, b) => (b.time || "").localeCompare(a.time || ""));
    return entries;
  }, [comments, acceptances, downloads, portalName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter
  const filtered = typeFilter === "all"
    ? allEntries
    : allEntries.filter((e) => e.type === typeFilter);

  // Paginate
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageEntries = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Group by date
  const grouped: { date: string; entries: TimelineEntry[] }[] = [];
  let currentDate = "";
  for (const entry of pageEntries) {
    if (entry.dateKey !== currentDate) {
      currentDate = entry.dateKey;
      grouped.push({ date: currentDate, entries: [] });
    }
    grouped[grouped.length - 1].entries.push(entry);
  }

  return (
    <main className="vault-panel vault-main-content" style={{ gridColumn: "1 / -1" }}>
      <div className="vault-hub-content">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <button className="vault-btn-ghost" onClick={() => navigate("/digibox")} style={{ fontSize: "var(--vault-text-small)", marginBottom: "var(--vault-spacing-xs)" }}>
              ← Overview
            </button>
            <h1 className="vault-heading-display">ATTIVITÀ</h1>
          </div>
          <span className="vault-label-tiny">{filtered.length} eventi</span>
        </div>

        {/* Type filter */}
        <div style={{ display: "flex", gap: "var(--vault-spacing-sm)", borderBottom: "1px solid var(--vault-grid-line)", paddingBottom: "var(--vault-spacing-xs)" }}>
          {FILTER_OPTIONS.map((opt) => (
            <span
              key={opt.key}
              className={`vault-nav-link ${typeFilter === opt.key ? "active" : ""}`}
              onClick={() => { setTypeFilter(opt.key); setPage(0); }}
            >
              {opt.label}
            </span>
          ))}
        </div>

        {/* Timeline grouped by date */}
        {pageEntries.length === 0 ? (
          <div className="vault-empty" style={{ padding: "var(--vault-spacing-lg)" }}>
            Nessuna attività trovata
          </div>
        ) : (
          <div>
            {grouped.map((group) => (
              <div key={group.date}>
                <div style={{
                  position: "sticky",
                  top: 0,
                  background: "var(--vault-bg)",
                  padding: "var(--vault-spacing-xs) 0",
                  zIndex: 5,
                  borderBottom: "1px solid var(--vault-grid-line)",
                }}>
                  <span className="vault-label-tiny" style={{ fontWeight: 600 }}>
                    {formatDate(group.date)}
                  </span>
                </div>
                <div className="vault-timeline" style={{ paddingTop: "var(--vault-spacing-xs)" }}>
                  {group.entries.map((entry) => (
                    <div key={entry.id} className="vault-timeline-item">
                      <div className={`vault-timeline-dot ${dotClass[entry.type] || ""}`}>
                        {dotIcon[entry.type]}
                      </div>
                      <div className="vault-timeline-content">
                        <div className="vault-timeline-header">
                          <span className="vault-label-tiny">
                            <strong>{entry.actor}</strong>{" "}
                            {actionLabel[entry.type]}{" "}
                            {entry.target ? (
                              <span className="vault-timeline-target" onClick={() => navigate(entry.target!.path)}>
                                {entry.target.name}
                              </span>
                            ) : null}
                          </span>
                          <span className="vault-label-tiny" style={{ flexShrink: 0 }}>
                            {timeAgo(entry.time)}
                          </span>
                        </div>
                        {entry.type === "comment" && entry.body && (
                          <div className="vault-timeline-body" style={{ WebkitLineClamp: 4 }}>
                            {renderMentions(entry.body)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "var(--vault-spacing-sm)",
            paddingTop: "var(--vault-spacing-md)",
            borderTop: "1px solid var(--vault-grid-line)",
          }}>
            <button
              className="vault-btn"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{ minWidth: 100, padding: "6px 16px" }}
            >
              ← Precedente
            </button>
            <span className="vault-label-tiny">
              {page + 1} / {totalPages}
            </span>
            <button
              className="vault-btn"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              style={{ minWidth: 100, padding: "6px 16px" }}
            >
              Successivo →
            </button>
          </div>
        )}
      </div>
    </main>
  );
};
